/**
 * TimeWindowToggle — segmented control for selecting a time window.
 *
 * Used by Market + Signals tabs (and likely future analytics surfaces).
 * Values are sinceMs (milliseconds back from now), so the parent can pass
 * them straight to API fetchers without further translation.
 *
 * Default presets cover the common dashboard windows; callers can supply
 * a custom array to override.
 */

import { cn } from '@/lib/cn';

export interface TimeWindowOption {
  label: string;
  sinceMs: number;
}

const DAY = 24 * 60 * 60 * 1000;

export const DEFAULT_WINDOWS: TimeWindowOption[] = [
  { label: '7d',  sinceMs: 7  * DAY },
  { label: '30d', sinceMs: 30 * DAY },
  { label: '60d', sinceMs: 60 * DAY },
  { label: '90d', sinceMs: 90 * DAY },
];

interface Props {
  value: number;
  onChange: (sinceMs: number) => void;
  windows?: TimeWindowOption[];
  /** Optional label rendered before the pills. */
  label?: string;
}

export default function TimeWindowToggle({ value, onChange, windows = DEFAULT_WINDOWS, label }: Props) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {label && <span className="text-slate-500">{label}</span>}
      <div className="inline-flex bg-slate-900 border border-slate-800 rounded p-0.5">
        {windows.map(w => (
          <button
            key={w.label}
            onClick={() => onChange(w.sinceMs)}
            className={cn(
              'px-2.5 py-1 rounded transition text-xs font-mono',
              value === w.sinceMs
                ? 'bg-slate-700 text-white'
                : 'text-slate-400 hover:text-white',
            )}
          >
            {w.label}
          </button>
        ))}
      </div>
    </div>
  );
}
